import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import { TableCell, TableRow } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useRef, useState } from 'react';

import type { AttachmentDto, ResourceIdDto } from '@/api/generated';

import { useDownloadFile } from '@/api/requests/files/useDownloadFile.util.ts';
import {
  NewEmailAttachmentsLoadingIconStyles,
  NewEmailAttachmentsTrashIconStyles,
  NewEmailAttachmentsTrashIconWrapperStyles,
} from '@/components/emailWindow/components/attachments/styles/NewEmailAttachments.styles.ts';
import { FileUploadDeletionConfirmationDialog } from '@/components/fileUpload/components/FileUploadDeletionConfirmationDialog.comp.tsx';
import { FileUploadItemStyles } from '@/components/fileUpload/styles/FileUpload.styles.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useGetSelectOptions } from '@/models/useGetSelectOptions.util.ts';
import { TextCutWithDotsStyles } from '@/styles/text.styles.ts';
import { formatDate } from '@/utils/formatDate.util.ts';

type FileDetails = {
  externalDiskId: string;
  externalFileId: string;
  resource: ResourceIdDto;
};

interface Props {
  file: AttachmentDto;
  isReady: boolean;
  onDeleteFile: (req: FileDetails) => Promise<void>;
}

export const FileUploadItem: React.FC<Props> = props => {
  const { file, isReady, onDeleteFile } = props;
  const [isBeingDeleted, setIsBeingDeleted] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const tdRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const { downloadFile, isDownloading } = useDownloadFile();
  const { resourceTypeOptions } = useGetSelectOptions();

  useEffect(() => {
    if (tdRef.current) {
      setIsTruncated(tdRef.current.scrollWidth > tdRef.current.clientWidth);
    }
  }, [file.name]);

  const handleDeleteAttachment = async () => {
    setIsBeingDeleted(true);
    try {
      await onDeleteFile({ resource: file.resourceId, ...file });
    } finally {
      setIsBeingDeleted(false);
    }
  };

  const handleShowDeleteDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setDeleteModalVisible(true);
  };
  const handleCloseDeleteDialog = () => {
    setDeleteModalVisible(false);
  };

  const handleDownload = () => {
    void (async () => {
      await downloadFile({ resource: file.resourceId, ...file });
    })();
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          background: isDownloading ? 'rgba(182,182,182,0.1)' : 'inherit',
          cursor: isDownloading ? 'progress' : 'default',
        }}
      >
        <TableCell>
          <Box
            onClick={handleDownload}
            ref={tdRef}
            sx={FileUploadItemStyles({ isDownloading, isReady })}
          >
            {isTruncated ? (
              <Tooltip sx={{ width: '100%' }} title={file.name}>
                <Box>{file.name}</Box>
              </Tooltip>
            ) : (
              file.name
            )}
          </Box>
        </TableCell>
        <TableCell sx={TextCutWithDotsStyles}>{file.created_by}</TableCell>
        <TableCell>
          {resourceTypeOptions.find(it => it.value === file.resourceId.type)?.label}
        </TableCell>
        <TableCell>{formatDate(file.created_at, { withTime: true })}</TableCell>
        <TableCell>{calculateFileSize(file.size)}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <DownloadButton
              isDownloading={isDownloading}
              isReady={isReady}
              onClick={handleDownload}
            />
            <DeleteButton
              isBeingDeleted={isBeingDeleted}
              isReady={isReady}
              onClick={handleShowDeleteDialog}
            />
          </Stack>
        </TableCell>
      </TableRow>
      {deleteModalVisible && (
        <FileUploadDeletionConfirmationDialog
          name={file.name}
          onCancel={handleCloseDeleteDialog}
          onConfirm={handleDeleteAttachment}
        />
      )}
    </>
  );
};

const DeleteButton: React.FC<{
  isBeingDeleted: boolean;
  isReady: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}> = props => {
  const { isBeingDeleted, isReady, onClick } = props;

  if (!isReady) {
    return (
      <IconButton disabled sx={NewEmailAttachmentsTrashIconWrapperStyles}>
        <DeleteForeverOutlinedIcon sx={NewEmailAttachmentsTrashIconStyles} />
      </IconButton>
    );
  }

  if (isBeingDeleted) {
    return <LoopIcon sx={NewEmailAttachmentsLoadingIconStyles} />;
  }
  return (
    <IconButton onClick={onClick} sx={NewEmailAttachmentsTrashIconWrapperStyles}>
      <DeleteForeverOutlinedIcon sx={NewEmailAttachmentsTrashIconStyles} />
    </IconButton>
  );
};

const DownloadButton: React.FC<{
  isDownloading: boolean;
  isReady: boolean;
  onClick: () => void;
}> = props => {
  const { isDownloading, isReady, onClick } = props;

  if (!isReady) {
    return (
      <IconButton disabled>
        <DownloadOutlinedIcon sx={{ color: themeColors.accentColor }} />
      </IconButton>
    );
  }

  if (isDownloading) {
    return <LoopIcon sx={NewEmailAttachmentsLoadingIconStyles} />;
  }

  return (
    <IconButton onClick={onClick}>
      <DownloadOutlinedIcon sx={{ color: themeColors.accentColor }} />
    </IconButton>
  );
};

const calculateFileSize = (size: number) => {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(0)} KB`;
  }
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
};
